import { Component, computed, effect, inject, signal, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';

import { EChartsOption } from 'echarts';
import { NgxEchartsDirective, provideEcharts } from 'ngx-echarts';

import { RegisterComponent } from '../../components/component-lookup';
import { VisualBaseComponent } from '../visual-base.component';

import { GroupByPipe } from '../../../shared/pipes/group-by.pipe';
import { forkJoin } from 'rxjs';
import { FilterCorrelationMetricPipe } from '../../pipes/filter-correlation-metric.pipe';

import { DateRange, StateService } from '../../services/state.service';
import { CorrelationMetric, InstitutionStatistical } from '../../services/metric.service';
import { CorrelationRate, RateService } from '../../services/rate.service';
import { VisualService } from '../../services/visual.service';
import { SessionService } from '../../../shared/services/session.service';


@RegisterComponent('HeatMapComponent')
@Component({
  selector: 'app-heat-map',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, NgxEchartsDirective, FilterCorrelationMetricPipe, GroupByPipe],
  templateUrl: './heat-map.component.html',
  styleUrl: './heat-map.component.scss',
  providers: [provideEcharts()]
})
export class HeatMapComponent extends VisualBaseComponent {
  readonly institution = inject(StateService).institution;
  readonly filteredGroup = inject(StateService).filteredGroup;
  readonly dateRange = inject(StateService).dateRange;

  private sessionService = inject(SessionService);
  private rateService = inject(RateService);
  readonly regressionFormula = signal<string>('');

  // Add two updateOptions objects
  updateOptions1: EChartsOption = {};
  updateOptions2: EChartsOption = {};

  // Track echarts instances for each chart
  echartsInstances: { [key: string]: any } = {};

  public override onChartInit(e: any, chartId?: string): void {
    if (chartId) {
      this.echartsInstances[chartId] = e;
    } else {
      this.echartsInstance = e; // Fallback to the base class behavior
    }

    e.showLoading({ color: '#38789e', maskColor: this.loadingColor });

    this.setOptions();
    this.chartInit.set(true);
  }

  override initOptions = { height: 400 }
  override baseOptions: EChartsOption = {
    grid: {
      left: 10,
      right: 25,
      top: 20,
      bottom: 40,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      show: true,
      axisLabel: {
        color: '#243b5b'
      }
    },
    yAxis: {
      type: 'category',
      show: true,
      axisLabel: {
        color: '#243b5b'
      }
    },
    axisLabel: {
      color: '#002f8e'
    },
    visualMap: {
      calculable: false,
      orient: 'horizontal',
      left: 'center',
      bottom: '15%',
      show: false
    },
    series: [
      {
        type: 'heatmap'
      },
      {
        type: 'line',
        symbol: 'none'
      },
      {
        type: 'line',
        symbol: 'none'
      }
    ]
  };
  override tooltipOptions: EChartsOption = {
    tooltip: {
      trigger: 'item',
      valueFormatter: value => VisualService.getValueDisplay(Number(value), this.metric()?.displayType)
    }
  };
  override printOptions: EChartsOption = {
    animation: false,
    series: [
      {
        type: 'scatter'
      },
      {
        type: 'line',
        symbol: 'none',
        symbolSize: 7,
        lineStyle: {
          type: [20, 5],
          dashOffset: 5,
        }
      },
      {
        type: 'line',
        symbol: 'none'
      }
    ]
  };

  readonly correlationMetrics = signal<CorrelationMetric[]>([]);
  readonly metric = signal<CorrelationMetric | undefined>(undefined);
  readonly correlationRates = computed(() => this.metric()?.rates ?? []);
  readonly rate = signal<CorrelationRate | undefined>(undefined);

  private statisticals?: InstitutionStatistical[];
  readonly statistical = signal<InstitutionStatistical | undefined>(undefined);
  readonly bestStatistical = signal<InstitutionStatistical | undefined>(undefined);
  readonly insufficientData = computed(() => this.statistical()?.beta == null);

  metricListOpen = false;
  rateListOpen = false;
  openMetricCategories: string[] = [];
  openRateCategories: string[] = [];
  metricSearch = '';
  rateSearch = '';
  noData = false;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // override resizedObserver = new ResizeObserver((entries) => {
  //   if (!this.echartsInstance?.isDisposed()) {
  //     this.echartsInstance?.resize();
  //   }
  // });

  constructor() {
    super();

    effect(() => {
      const institution = this.institution();
      if (institution?.institutionType) {
        this.metricService.getCorrelationMetrics(institution.institutionType).subscribe(metrics => {
          setTimeout(() => {
            const depositsCategory = '2. Deposit and Borrowing Costs (QTR - Annualized)';
            this.correlationMetrics.set(metrics.filter(metric => metric.category.trim() === depositsCategory));

            const metricId = this.sessionService.get(`correlation-metricId-${this.institution()?.institutionType}`)?.toString() ?? this.metric()?.metricId ?? metrics[0]?.metricId;
            this.selectMetric(metricId);
          });
        });
      }
    });

    effect(() => {
      const dateRange = this.dateRange();
      const metric = this.metric();
      const group = this.filteredGroup();
      const rate = this.rate();
      if (this.chartInit() && dateRange && metric && group && rate) {
        this.updateVisual(dateRange, metric, rate);
      }
    });
  }

  public updateVisual(dateRange: DateRange, metric: CorrelationMetric, rate: CorrelationRate) {
    this.metricService.getInstitutionStatisticals(this.institution().iid, metric.metricId, dateRange).subscribe(statisticals => {
      this.statisticals = statisticals;
      setTimeout(() => {
        this.statistical.set(this.statisticals?.find(s => s.rateId == rate.rateId) ?? {} as InstitutionStatistical);
        this.bestStatistical.set(this.statisticals?.reduce((p, c) => ((p.rSquared ?? 0) > (c.rSquared ?? 0)) ? p : c, {} as InstitutionStatistical) ?? {} as InstitutionStatistical);

      });
    });

    forkJoin({
      metricData: this.metricService.getMetricData(this.institution().iid, metric.metricId, undefined),
      rateData: this.rateService.getRateData(rate.rateId)
    }).subscribe(({ metricData, rateData }) => {
      const metricDataSeries = this.visualService.getMetricSeriesPeer(this.metric()?.displayType, metricData, dateRange);

      const rateSeries: any[] = [];
      rateData.forEach((data: { reportDate: Date; value: number; }) => {
        const dataRow = metricDataSeries.dates.findIndex(d => { return new Date(d).getTime() == new Date(data.reportDate).getTime() });
        if (dataRow > -1) {
          rateSeries[dataRow] = Number(data.value).toFixed(2);
        }
      });

      if (metricDataSeries.institutionSeries.filter(v => v != null).length == 0) {
        this.echartsInstance?.clear();
        this.chartOptions = this.noDataOptions;
        this.noData = true;
      }
      else {
        if (this.noData) {
          this.setOptions();
          this.noData = false;
        }
        const rateNames = ['10yr', '5yr', '3mo', 'FedFunds'];

        const lags = ['0 Lag', '3-mo Lag', '6-mo Lag'];

        const rateNameToCoordinates: Record<string, [number, number]> = {
          '10-year Treasury': [0, 0],
          '10-year Treasury (3-mo lag)': [0, 1],
          '10-year Treasury (6-mo lag)': [0, 2],
          '5-year Treasury': [1, 0],
          '5-year Treasury (3-mo lag)': [1, 1],
          '5-year Treasury (6-mo lag)': [1, 2],
          '3-month Treasury': [2, 0],
          '3-month Treasury (3-mo lag)': [2, 1],
          '3-month Treasury (6-mo lag)': [2, 2],
          'Fed Funds': [3, 0],
          'Fed Funds (3-Mo Lag)': [3, 1],
          'Fed Funds (6-Mo Lag)': [3, 2],
        };


        const rateBetaMap: Record<string, number | string> =
          this.statisticals?.reduce<Record<string, number | string>>((map, rate) => {
            map[rate.rateId] = map[rate.rateId] = rate.beta !== undefined ? rate.beta.toFixed(2) : '-';
            return map;
          }, {}) || {};

        const rateRsquaredMap: Record<string, number | string> =
          this.statisticals?.reduce<Record<string, number | string>>((map, rate) => {
            map[rate.rateId] = map[rate.rateId] = rate.rSquared !== undefined ? rate.rSquared.toFixed(2) : '-';
            return map;
          }, {}) || {};

        // Get the minimum and maximum values from the beta values and rsquared values
        const betaValues = Object.values(rateBetaMap)
          .filter(value => value !== '-' && !isNaN(Number(value)))
          .map(value => Number(value));

        const minBeta = betaValues.length > 0 ? Math.min(...betaValues) : 0;
        const maxBeta = betaValues.length > 0 ? Math.max(...betaValues) : 0;

        const rsquaredValues = Object.values(rateRsquaredMap)
          .filter(value => value !== '-' && !isNaN(Number(value)))
          .map(value => Number(value));

        const minRsquared = rsquaredValues.length > 0 ? Math.min(...rsquaredValues) : 0;
        const maxRsquared = rsquaredValues.length > 0 ? Math.max(...rsquaredValues) : 0;

        // Generate heat data
        const betaMapped = this.correlationRates()
          .map(({ rateId, rateName }) => {
            const beta = rateBetaMap[rateId] ?? '-';

            if (!(rateName.trim() in rateNameToCoordinates)) return null;
            const coordinates = rateNameToCoordinates[rateName.trim() as keyof typeof rateNameToCoordinates];

            return [...coordinates, beta];
          })
          .filter((item): item is [number, number, string | number] => item !== null);

        const rsquaredMapped = this.correlationRates()
          .map(({ rateId, rateName }) => {
            const rsquared = rateRsquaredMap[rateId] ?? '-';

            if (!(rateName.trim() in rateNameToCoordinates)) return null;
            const coordinates = rateNameToCoordinates[rateName.trim() as keyof typeof rateNameToCoordinates];

            return [...coordinates, rsquared];
          })
          .filter((item): item is [number, number, string | number] => item !== null);

        this.updateOptions1 = {
          tooltip: {
            position: 'top'
          },
          grid: {
            height: '50%',
            top: '10%'
          },
          xAxis: {
            type: 'category',
            data: rateNames,
            splitArea: {
              show: true
            }
          },
          yAxis: {
            type: 'category',
            data: lags,
            splitArea: {
              show: true
            }
          },
          visualMap: {
            min: minBeta,
            max: maxBeta,
            calculable: false,
            orient: 'horizontal',
            left: 'center',
            bottom: '15%',
            show: false,
            inRange: {
              color: ['#ffffff','#006400'] // Dark green to white
            }
          },
          series: [
            {
              name: 'Beta',
              type: 'heatmap',
              data: betaMapped,
              label: {
                show: true
              },
              emphasis: {
                itemStyle: {
                  shadowBlur: 10,
                  shadowColor: 'rgba(0, 0, 0, 0.5)'
                }
              }
            }
          ]
        };

        this.updateOptions2 = {
          tooltip: {
            position: 'top'
          },
          grid: {
            height: '50%',
            top: '10%'
          },
          xAxis: {
            type: 'category',
            data: rateNames,
            splitArea: {
              show: true
            }
          },
          yAxis: {
            type: 'category',
            data: lags,
            splitArea: {
              show: true
            }
          },
          visualMap: {
            min: minRsquared,
            max: maxRsquared,
            calculable: false,
            orient: 'horizontal',
            left: 'center',
            bottom: '15%',
            show: false,
            inRange: {
              color: ['#006400','#ffffff'] // Dark green to white
            }
          },
          series: [
            {
              name: 'Rsquared',
              type: 'heatmap',
              data: rsquaredMapped,
              label: {
                show: true
              },
              emphasis: {
                itemStyle: {
                  shadowBlur: 10,
                  shadowColor: 'rgba(0, 0, 0, 0.5)'
                }
              }
            }
          ]
        };
      }
      Object.values(this.echartsInstances).forEach(instance => instance?.hideLoading());
    });
  }

  clearMetricSearch() {
    this.metricSearch = '';
    this.openMetricCategories = [];
  }

  clearRateSearch() {
    this.rateSearch = '';
    this.openRateCategories = [];
  }

  isMetricCategoryOpen(category: string) {
    return this.openMetricCategories.find(c => c == category);
  }

  isRateCategoryOpen(category: string) {
    return this.openRateCategories.find(c => c == category);
  }

  toggleMetricCategory(category: string) {
    if (this.openMetricCategories.find(c => c == category)) {
      this.openMetricCategories = this.openMetricCategories.filter(c => c != category);
    }
    else {
      this.openMetricCategories.push(category);
    }
  }

  toggleRateCategory(category: string) {
    if (this.openRateCategories.find(c => c == category)) {
      this.openRateCategories = this.openRateCategories.filter(c => c != category);
    }
    else {
      this.openRateCategories.push(category);
    }
  }

  selectMetric(metricId: string): void {
    const metric = this.correlationMetrics().find(m => m.metricId == metricId);
    if (metric) {
      this.sessionService.set(`correlation-metricId-${this.institution()?.institutionType}`, metricId, true);
      this.metric.set(metric);
      this.selectRate(undefined);
      this.metricListOpen = false;
    }
  }

  selectRate(rateId: string | undefined): void {
    let rate: CorrelationRate | undefined = undefined;
    const key = `correlation-rateId-${this.metric()?.metricId}${this.institution()?.institutionType.toString()}`;

    if (rateId) {
      rate = this.correlationRates().find(r => r.rateId == rateId);
    }

    if (rate == undefined) {
      rateId = (this.sessionService.get(key) as CorrelationRate)?.rateId;
      rate = this.correlationRates().find(r => r.rateId == rateId);
    }

    if (rate == undefined) {
      rate = this.correlationRates().find(r => r.correlationsDefault == true);
    }

    if (rate == undefined) {
      rate = this.correlationRates()[0];
    }

    if (rate) {
      this.rate.set(rate);
      this.sessionService.set(key, rate, true);
      this.rateListOpen = false;
    }
  }
}

